import { Badge } from 'src/components/Badge';
import { Info } from 'src/components/Info';
import _ from 'lodash';
import {
  formatValue,
  statusToBadgeVariant,
  statusToLabel,
} from 'src/components/callAnalysis/callAnalysis.helpers';
import {
  NormalizedPostCallAnalysisResult,
  PostCallAnalysisResultItemStatus,
} from 'src/interfaces/agent.interface';
import { formatSnakeCase } from 'src/utils/string';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  _.isPlainObject(value);

export const isPostCallResultsExtractionMap = (
  value: unknown,
): value is NormalizedPostCallAnalysisResult => {
  if (!isPlainObject(value)) return false;

  const entries = Object.entries(value);
  if (entries.length === 0) return false;

  const allowedStatuses = new Set<string>(
    Object.values(PostCallAnalysisResultItemStatus),
  );

  return entries.every(([, item]) => {
    if (!isPlainObject(item)) return false;

    const status = item.status;
    if (typeof status !== 'string') return false;

    if (!allowedStatuses.has(status)) return false;

    const label = item.label;
    return typeof label === 'string' && label.trim() !== '';
  });
};

export const PostCallResultsRenderer = ({
  results,
}: {
  results: NormalizedPostCallAnalysisResult;
}) => {
  return (
    <div className="space-y-3">
      {Object.entries(results).map(([id, data]) => {
        const label = data.label.trim() || formatSnakeCase(id);
        const statusText = statusToLabel(data.status);
        const isExtracted =
          data.status === PostCallAnalysisResultItemStatus.EXTRACTED;
        const rationale =
          typeof data.rationale === 'string' && data.rationale.trim() !== ''
            ? data.rationale
            : null;

        return (
          <div
            key={id}
            className="bg-neutral-50 border border-neutral-100 rounded-lg p-3"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-500 uppercase">
                  {label}
                </span>
                {isExtracted ? (
                  <span className="font-bold text-neutral-700">
                    {formatValue(data.value)}
                  </span>
                ) : (
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={statusToBadgeVariant(data.status)}
                      className="rounded-full px-4"
                    >
                      {statusText}
                    </Badge>
                    {rationale && <Info value={rationale} />}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
